import { FC, isValidElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, SvgIcon, Typography } from '@mui/material';

import { AnimatedReveal } from '../../../atoms/AnimatedReveal/AnimatedReveal.styled';
import ClockIcon from '../../../icons/ClockIcon.svg';
import { IRideHailingServiceLabel } from '../utils/deriveRideHailingLabels';

import { ServiceCardContentLayout } from './ServiceCardContentLayout/ServiceCardContentLayout';
import { PriceText } from './PriceText';
import { RideHailingLabel } from './RideHailingLabel';
interface IRideHailingCardContentProps {
	icon: ReactNode | null;
	transportTypesText: ReactNode;
	shouldShowLabels?: boolean;
	labels?: IRideHailingServiceLabel[];
	testId?: string;
	priceNode?: ReactNode;
	timeNode?: ReactNode;
	/** This means that the CC is SUCCESS */
	hasReservation?: boolean;
	showDescription?: boolean;
	rideDescriptionKey?: string;
}

export const RideHailingCardContent: FC<IRideHailingCardContentProps> = ({
	shouldShowLabels = false,
	icon,
	transportTypesText,
	labels,
	testId = 'ride-hailing-card-content',
	priceNode,
	timeNode,
	hasReservation,
	showDescription = false,
	rideDescriptionKey,
}) => {
	const { t } = useTranslation(['common']);

	const safeLabels = labels ?? [];

	const titleContent = isValidElement(transportTypesText) ? (
		transportTypesText
	) : (
		<Typography variant="b1-sb" color="primary.main">
			{transportTypesText}
		</Typography>
	);

	return (
		<ServiceCardContentLayout
			icon={icon}
			leftContent={
				<Stack spacing={0} flex="1 1 auto" minWidth={0} justifyContent="center">
					{titleContent}

					{shouldShowLabels && (
						<Stack direction="row" alignItems="center" gap={0.5} mt={0.5}>
							<SvgIcon component={ClockIcon} />

							<Typography variant="l1" color="primary.main">
								{t('booking:text.footpath_includedV2')}
							</Typography>
						</Stack>
					)}
				</Stack>
			}
			rightContent={
				<Stack alignItems="flex-end" gap={0.5} flex="0 0 auto">
					{priceNode && (
						<PriceText data-testid="route-ridePrice-block">
							{priceNode}
						</PriceText>
					)}

					{timeNode && (
						<Typography
							data-testid="route-routeTime-block"
							variant="l1"
							color={hasReservation ? 'primary' : 'solutions.600'}
							textAlign="right"
							noWrap
						>
							{timeNode}
						</Typography>
					)}
				</Stack>
			}
			bottomContent={
				<>
					{safeLabels.length > 1 && (
						<RideHailingLabel labels={safeLabels} testId={testId} />
					)}

					{rideDescriptionKey && (
						<AnimatedReveal open={showDescription} mt={showDescription ? 1 : 0}>
							<Typography variant="b2">
								{t(`booking:travel_types.${rideDescriptionKey}.description`)}
							</Typography>
						</AnimatedReveal>
					)}
				</>
			}
		/>
	);
};
